import { Link } from "react-router-dom";
import type { BlogPostSummary, Photo, Video } from "../api/types";
import { getFeaturedPhotos, getFeaturedVideos, getPosts } from "../api/content";
import { useAsync } from "../hooks/useAsync";
import { useRotatingWindow } from "../hooks/useRotatingWindow";
import { accession, formatDate, formatYear } from "../lib/format";
import { ContactFrame } from "../components/media/ContactFrame";

/** Pinned to the hero; served from frontend/public/files. */
const HEADSHOT = "/files/casey-scott-mckay-headshot-suspenders.jpg";

/** Rotate sections every two minutes so visitors see the whole collection over time. */
const ROTATE_MS = 120_000;

/** Featured strip of photographs, shown as a contact sheet. */
function FeaturedSheet({ photos }: { photos: Photo[] }) {
  if (photos.length === 0) return null;
  return (
    <div className="sheet">
      {photos.map((photo, i) => (
        <ContactFrame
          key={photo.id}
          index={i + 1}
          code={accession("PHOTO", photo.id)}
          title={photo.title}
          caption={formatYear(photo.takenAt) || undefined}
          imageUrl={photo.thumbnailUrl ?? photo.url}
          to={`/documents/PHOTO-${photo.id}`}
          visibility={photo.visibility}
        />
      ))}
    </div>
  );
}

/** Featured strip of videos, contact-sheet style with a play glyph. */
function FeaturedVideos({ videos }: { videos: Video[] }) {
  if (videos.length === 0) return null;
  return (
    <div className="sheet">
      {videos.map((video, i) => (
        <ContactFrame
          key={video.id}
          index={i + 1}
          code={accession("VIDEO", video.id)}
          title={video.title}
          caption={formatDate(video.publishedAt) || undefined}
          imageUrl={video.thumbnailUrl}
          isVideo
          to={`/documents/VIDEO-${video.id}`}
          visibility={video.visibility}
        />
      ))}
    </div>
  );
}

/** Recent blog posts as cards. */
function RecentPosts({ posts }: { posts: BlogPostSummary[] }) {
  if (posts.length === 0) return null;
  return (
    <div className="post-list">
      {posts.map((post) => (
        <Link key={post.id} to={`/blog/${post.slug}`} className="post-card" aria-label={post.title}>
          {post.coverImageUrl && (
            <div className="post-card__cover">
              <img src={post.coverImageUrl} alt={post.title} loading="lazy" />
            </div>
          )}
          <div className="post-card__body">
            <p className="accession accession--ink">{accession("BLOG_POST", post.id)}</p>
            <h3 className="post-card__title">{post.title}</h3>
            {post.excerpt && <p className="post-card__excerpt">{post.excerpt}</p>}
            {post.publishedAt && (
              <p className="mono" style={{ color: "var(--ink-500)", fontSize: "0.7rem" }}>
                {formatDate(post.publishedAt)}
              </p>
            )}
          </div>
        </Link>
      ))}
    </div>
  );
}

/**
 * Landing page: a plain welcome, then three sections that rotate — the
 * featured photos and videos (chosen and ordered on the admin console's Front
 * page tab) and the latest posts — advancing every couple of minutes so there's
 * always something new without anything flashing by.
 */
export function HomePage() {
  const { data: photoData } = useAsync(() => getFeaturedPhotos(0, 60), []);
  const { data: postData } = useAsync(() => getPosts(0, 60), []);
  const { data: videoData } = useAsync(() => getFeaturedVideos(0, 60), []);

  const photos = useRotatingWindow(photoData?.items ?? [], 8, ROTATE_MS);
  const posts = useRotatingWindow(postData?.items ?? [], 3, ROTATE_MS);
  const videos = useRotatingWindow(videoData?.items ?? [], 4, ROTATE_MS);

  return (
    <>
      <div className="marquee" aria-hidden="true">
        <span>
          ★ Welcome to csmckay.us ★ New videos posted from the YouTube channel ★
          Now with searchable photos, videos &amp; posts ★ Sign the guestbook ★
          Best viewed in Netscape Navigator ★ Thanks for visiting! ★
        </span>
      </div>

      <section className="hero">
        <div className="container hero__inner">
          <figure className="hero__portrait">
            <img src={HEADSHOT} alt="Casey Scott McKay" width={480} height={720} fetchPriority="high" />
            <figcaption className="mono">Casey Scott McKay</figcaption>
          </figure>
          <p className="eyebrow">csmckay.us</p>
          <h1>Welcome</h1>
          <p className="hero__lead">
            This is the personal website of Casey Scott McKay with photos,
            videos, and posts.
          </p>
          <div className="hero__meta">
            <Link to="/photos" className="btn">
              Browse
            </Link>
            <Link to="/blog" className="btn btn--ghost">
              Read
            </Link>
            <Link to="/search" className="btn btn--ghost">
              Search
            </Link>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <div>
              <p className="eyebrow">Collection 01 · Photos · rotating</p>
              <h2>From the photos</h2>
            </div>
            <Link to="/photos">View all →</Link>
          </div>
          <FeaturedSheet photos={photos} />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <div>
              <p className="eyebrow">Collection 02 · Videos · rotating</p>
              <h2>From the videos</h2>
            </div>
            <Link to="/videos">View all →</Link>
          </div>
          <FeaturedVideos videos={videos} />
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="section__head">
            <div>
              <p className="eyebrow">Posts · Index BL · rotating</p>
              <h2>From the blog</h2>
            </div>
            <Link to="/blog">All posts →</Link>
          </div>
          <RecentPosts posts={posts} />
        </div>
      </section>
    </>
  );
}
